<template>
  <div class="base-image-cropper">
    <div class="base-image-cropper__container">
      <!-- Большое изображение -->
      <div
        ref="cropperContainer"
        class="base-image-cropper__image-container"
        @wheel="handleWheel"
      >
        <img
          v-if="imageSrc"
          ref="imageElement"
          class="base-image-cropper__image"
          :src="imageSrc"
          alt="Image to crop"
          :style="imageStyle"
          @load="onImageLoad"
          @error="onImageError"
        />

        <div
          v-if="loading"
          class="base-image-cropper__loading-overlay"
        >
          <BaseLoader2 :size="50" />
          <p>{{ $i18n.t("common.loading") }}</p>
        </div>

        <!-- Crop overlay - показываем только когда не загружается -->
        <div
          v-if="!loading && !error"
          class="crop-overlay"
        >
          <!-- Подвижная и изменяемая рамка -->
          <div
            class="crop-box"
            :class="{ 'crop-box--circular': circularOverlay }"
            :style="cropBoxStyle"
            @mousedown="startDragCropBox"
            @touchstart="startDragCropBox"
          >
            <!-- Для круглой рамки показываем только 4 ручки по краям окружности -->
            <template v-if="circularOverlay">
              <!-- Верх -->
              <div
                class="resize-handle circular-top"
                @mousedown="startResize('top', $event)"
                @touchstart="startResize('top', $event)"
              />
              <!-- Право -->
              <div
                class="resize-handle circular-right"
                @mousedown="startResize('right', $event)"
                @touchstart="startResize('right', $event)"
              />
              <!-- Низ -->
              <div
                class="resize-handle circular-bottom"
                @mousedown="startResize('bottom', $event)"
                @touchstart="startResize('bottom', $event)"
              />
              <!-- Лево -->
              <div
                class="resize-handle circular-left"
                @mousedown="startResize('left', $event)"
                @touchstart="startResize('left', $event)"
              />
            </template>

            <!-- Для прямоугольной рамки показываем все ручки -->
            <template v-else>
              <!-- Угловые ручки -->
              <div
                class="resize-handle top-left"
                @mousedown="startResize('top-left', $event)"
                @touchstart="startResize('top-left', $event)"
              />
              <div
                class="resize-handle top-right"
                @mousedown="startResize('top-right', $event)"
                @touchstart="startResize('top-right', $event)"
              />
              <div
                class="resize-handle bottom-left"
                @mousedown="startResize('bottom-left', $event)"
                @touchstart="startResize('bottom-left', $event)"
              />
              <div
                class="resize-handle bottom-right"
                @mousedown="startResize('bottom-right', $event)"
                @touchstart="startResize('bottom-right', $event)"
              />

              <!-- Боковые ручки -->
              <div
                class="resize-handle top-center"
                @mousedown="startResize('top', $event)"
                @touchstart="startResize('top', $event)"
              />
              <div
                class="resize-handle bottom-center"
                @mousedown="startResize('bottom', $event)"
                @touchstart="startResize('bottom', $event)"
              />
              <div
                class="resize-handle left-center"
                @mousedown="startResize('left', $event)"
                @touchstart="startResize('left', $event)"
              />
              <div
                class="resize-handle right-center"
                @mousedown="startResize('right', $event)"
                @touchstart="startResize('right', $event)"
              />
            </template>

            <!-- Центральный крестик для круглой рамки -->
            <div
              v-if="circularOverlay"
              class="crop-box-center"
            >
              <div class="crop-box-crosshair"></div>
            </div>
          </div>
        </div>
      </div>

      <div class="base-image-cropper__controls">
        <div class="base-image-cropper__action-buttons">
          <BaseButton2
            class="ui-link-secondary ui-link-small"
            :disabled="loading"
            @click="rotateImage"
          >
            <BaseIcon2
              id="icon-rotate"
              class="icon__button-crop"
              height="16"
              width="16"
            />
            {{ $i18n.t("common.rotate") }}
          </BaseButton2>

          <BaseButton2
            class="ui-link-secondary ui-link-small"
            :disabled="loading"
            @click="resetAll"
          >
            <BaseIcon2
              id="icon-cross"
              class="icon__button-crop"
              height="16"
              width="16"
            />
            {{ $i18n.t("common.reset") }}
          </BaseButton2>
        </div>
      </div>
    </div>

    <!-- Показываем ошибку если есть -->
    <div
      v-if="error"
      class="base-image-cropper__error"
    >
      <p class="base-image-cropper__error-message">
        {{ error }}
      </p>
    </div>

    <div class="base-input-file__cropper-actions">
      <BaseButton2
        class="base-button-big base-button-first"
        :disabled="processing || loading"
        :loading="processing"
        @click="handleCrop"
      >
        {{ $i18n.t("common.ready") }}
      </BaseButton2>
      <BaseButton2
        class="base-button-big base-button-second"
        @click="handleClose"
      >
        {{ $i18n.t("common.cancel") }}
      </BaseButton2>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { heicTo } from "heic-to";
import type { CropResult } from "~/layers/base/stores/useImageCropper";

const { $i18n } = useNuxtApp();

interface Props {
  id: string;
  file?: File;
  title?: string;
  aspectRatio?: number;
  outputOptions?: {
    width?: number;
    height?: number;
    quality?: number;
    mimeType?: string;
  };
  circularOverlay?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  aspectRatio: 1, // По умолчанию квадрат
  outputOptions: () => ({}),
  circularOverlay: false,
});

const emits = defineEmits<{
  close: [];
  crop: [result: CropResult];
  error: [error: string];
  reset: [];
}>();

const imageElement = ref<HTMLImageElement | null>(null);
const cropperContainer = ref<HTMLDivElement | null>(null);
const imageSrc = ref<string>("");
const loading = ref(true);
const processing = ref(false);
const error = ref<string>("");

// Состояние изображения (фиксированное)
const imageState = ref({
  naturalWidth: 0,
  naturalHeight: 0,
  displayWidth: 0,
  displayHeight: 0,
  x: 0,
  y: 0,
  rotation: 0,
});

// Состояние рамки обрезки (подвижная) - всегда квадратная для круглой рамки
const cropBox = ref({
  x: 50,
  y: 50,
  width: 200,
  height: 200,
});

// Размеры контейнера
const containerSize = ref({
  width: 600,
  height: 400,
});

// Состояние перетаскивания
const isDraggingCropBox = ref(false);
const isResizing = ref(false);
const resizeHandle = ref<string>("");
const dragStart = ref({ x: 0, y: 0, cropX: 0, cropY: 0, cropWidth: 0, cropHeight: 0 });

// Стили для изображения (фиксированное)
const imageStyle = computed(() => ({
  width: `${imageState.value.displayWidth}px`,
  height: `${imageState.value.displayHeight}px`,
  transform: `translate(${imageState.value.x}px, ${imageState.value.y}px) rotate(${imageState.value.rotation}deg)`,
  transformOrigin: "center center",
}));

// Стили для рамки обрезки (подвижная)
const cropBoxStyle = computed(() => ({
  left: `${cropBox.value.x}px`,
  top: `${cropBox.value.y}px`,
  width: `${cropBox.value.width}px`,
  height: `${cropBox.value.height}px`,
}));

// Конвертация HEIC в JPEG используя heic-to
const convertHeicToJpeg = async (file: File): Promise<File> => {
  try {
    // heic-to принимает объект с blob и type
    const convertedBlob = await heicTo({
      blob: file,
      type: "image/jpeg",
      quality: 0.92,
    });

    // Создаем новый файл из блоба
    const fileName = file.name.replace(/\.(heic|heif)$/i, ".jpg");
    return new File([convertedBlob], fileName, { type: "image/jpeg" });
  } catch (err: any) {
    console.error("Failed to convert HEIC with heic-to:", err);

    // Проверяем специфичные ошибки HEIC конвертации
    if (err?.message?.includes("not supported") || err?.message?.includes("failed")) {
      throw new Error("HEIC формат не поддерживается. Попробуйте сделать фото в формате JPEG (в настройках iPhone: Настройки → Камера → Форматы → Наиболее совместимый)");
    }

    throw new Error("Не удалось конвертировать HEIC изображение. Попробуйте использовать формат JPEG или PNG.");
  }
};

const initializeCropper = async () => {
  if (!props.file) {
    error.value = "Файл не предоставлен";
    loading.value = false;
    return;
  }

  try {
    loading.value = true;
    error.value = "";

    // Очищаем предыдущий URL если есть
    if (imageSrc.value) {
      URL.revokeObjectURL(imageSrc.value);
      imageSrc.value = "";
    }

    let fileToProcess = props.file;

    // Проверяем и конвертируем HEIC файлы
    if (props.file.type === "image/heic" || props.file.type === "image/heif" || props.file.name.toLowerCase().endsWith(".heic") || props.file.name.toLowerCase().endsWith(".heif")) {
      try {
        fileToProcess = await convertHeicToJpeg(props.file);
      } catch (conversionError: any) {
        error.value = conversionError?.message || "Не удалось конвертировать HEIC изображение";
        loading.value = false;
        emits("error", error.value);
        return;
      }
    }

    // Проверяем, что это изображение
    if (!fileToProcess.type.startsWith("image/")) {
      error.value = "Неверный формат файла";
      loading.value = false;
      return;
    }

    // Создаем URL только для сконвертированного файла
    imageSrc.value = URL.createObjectURL(fileToProcess);

    await nextTick();
    await nextTick();
    await nextTick();

    if (!imageElement.value) {
      throw new Error("Image element not found after nextTick");
    }
  } catch (err) {
    console.error("Failed to initialize cropper:", err);
    error.value = "Ошибка инициализации";
    loading.value = false;
  }
};

const onImageLoad = () => {
  if (!imageElement.value) {
    console.error("Image element not found on load");
    return;
  }

  const img = imageElement.value;

  if (img.naturalWidth === 0 || img.naturalHeight === 0) {
    console.error("Image has zero dimensions");
    error.value = "Изображение имеет нулевые размеры";
    loading.value = false;
    return;
  }

  // Получаем реальные размеры контейнера
  if (cropperContainer.value) {
    const rect = cropperContainer.value.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      containerSize.value = {
        width: rect.width,
        height: rect.height,
      };
    } else {
      // Fallback если размеры не получены
      containerSize.value = {
        width: 300,
        height: 200,
      };
    }
  } else {
    // Fallback если контейнер недоступен
    containerSize.value = {
      width: 300,
      height: 200,
    };
  }

  // Сохраняем натуральные размеры
  imageState.value.naturalWidth = img.naturalWidth;
  imageState.value.naturalHeight = img.naturalHeight;

  // Вычисляем размеры для отображения (изображение вписывается в контейнер)
  const containerWidth = containerSize.value.width;
  const containerHeight = containerSize.value.height;
  const imageAspectRatio = img.naturalWidth / img.naturalHeight;
  const containerAspectRatio = containerWidth / containerHeight;

  let displayWidth, displayHeight;

  // Вписываем изображение в контейнер с сохранением пропорций
  if (imageAspectRatio > containerAspectRatio) {
    // Изображение шире контейнера - подгоняем по ширине
    displayWidth = containerWidth;
    displayHeight = containerWidth / imageAspectRatio;
  } else {
    // Изображение выше контейнера - подгоняем по высоте
    displayHeight = containerHeight;
    displayWidth = containerHeight * imageAspectRatio;
  }

  imageState.value.displayWidth = displayWidth;
  imageState.value.displayHeight = displayHeight;

  // Всегда центрируем изображение в контейнере
  imageState.value.x = (containerWidth - displayWidth) / 2;
  imageState.value.y = (containerHeight - displayHeight) / 2;

  // Сбрасываем поворот при загрузке
  imageState.value.rotation = 0;

  // Инициализируем рамку обрезки с максимальным размером
  initializeCropBox();

  loading.value = false;
};

const initializeCropBox = () => {
  const imageX = imageState.value.x;
  const imageY = imageState.value.y;
  const imageWidth = imageState.value.displayWidth;
  const imageHeight = imageState.value.displayHeight;

  // Для круглой рамки всегда делаем квадрат
  if (props.circularOverlay) {
    // Вычисляем максимальный квадрат, который поместится в изображение
    const maxSize = Math.min(imageWidth, imageHeight);

    // Центрируем квадрат на изображении
    cropBox.value = {
      x: imageX + (imageWidth - maxSize) / 2,
      y: imageY + (imageHeight - maxSize) / 2,
      width: maxSize,
      height: maxSize,
    };
  } else {
    // Для прямоугольной рамки используем переданное соотношение сторон
    let cropWidth, cropHeight;

    // Пробуем подогнать по ширине изображения
    cropWidth = imageWidth;
    cropHeight = cropWidth / props.aspectRatio;

    // Если высота не помещается, подгоняем по высоте изображения
    if (cropHeight > imageHeight) {
      cropHeight = imageHeight;
      cropWidth = cropHeight * props.aspectRatio;
    }

    // Центрируем рамку на изображении
    cropBox.value = {
      x: imageX + (imageWidth - cropWidth) / 2,
      y: imageY + (imageHeight - cropHeight) / 2,
      width: cropWidth,
      height: cropHeight,
    };
  }
};

const onImageError = () => {
  console.error("Image failed to load");
  error.value = "Ошибка загрузки изображения";
  loading.value = false;
};

// Функция полного сброса (рамка обрезки + поворот)
const resetAll = () => {
  // Сбрасываем поворот изображения
  imageState.value.rotation = 0;

  // Сбрасываем рамку обрезки
  initializeCropBox();

  emits("reset");
};

const rotateImage = () => {
  imageState.value.rotation = (imageState.value.rotation + 90) % 360;
};

const handleWheel = (event: WheelEvent) => {
  event.preventDefault();
};

// Перетаскивание рамки
const startDragCropBox = (e: MouseEvent | TouchEvent) => {
  if (loading.value || isResizing.value) return;

  e.preventDefault();
  e.stopPropagation();
  isDraggingCropBox.value = true;

  const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
  const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;

  dragStart.value = {
    x: clientX,
    y: clientY,
    cropX: cropBox.value.x,
    cropY: cropBox.value.y,
    cropWidth: cropBox.value.width,
    cropHeight: cropBox.value.height,
  };

  document.addEventListener("mousemove", handleDragCropBox);
  document.addEventListener("mouseup", stopDragCropBox);
  document.addEventListener("touchmove", handleDragCropBox, { passive: false });
  document.addEventListener("touchend", stopDragCropBox);
};

const handleDragCropBox = (e: MouseEvent | TouchEvent) => {
  if (!isDraggingCropBox.value) return;

  e.preventDefault();

  const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
  const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;

  const deltaX = clientX - dragStart.value.x;
  const deltaY = clientY - dragStart.value.y;

  cropBox.value.x = dragStart.value.cropX + deltaX;
  cropBox.value.y = dragStart.value.cropY + deltaY;

  constrainCropBox();
};

const stopDragCropBox = () => {
  isDraggingCropBox.value = false;

  document.removeEventListener("mousemove", handleDragCropBox);
  document.removeEventListener("mouseup", stopDragCropBox);
  document.removeEventListener("touchmove", handleDragCropBox, { passive: false } as EventListenerOptions);
  document.removeEventListener("touchend", stopDragCropBox);
};

// Изменение размера рамки
const startResize = (handle: string, e: MouseEvent | TouchEvent) => {
  if (loading.value) return;

  e.preventDefault();
  e.stopPropagation();

  isResizing.value = true;
  resizeHandle.value = handle;

  const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
  const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;

  dragStart.value = {
    x: clientX,
    y: clientY,
    cropX: cropBox.value.x,
    cropY: cropBox.value.y,
    cropWidth: cropBox.value.width,
    cropHeight: cropBox.value.height,
  };

  document.addEventListener("mousemove", handleResize);
  document.addEventListener("mouseup", stopResize);
  document.addEventListener("touchmove", handleResize, { passive: false });
  document.addEventListener("touchend", stopResize);
};

const handleResize = (e: MouseEvent | TouchEvent) => {
  if (!isResizing.value) return;

  e.preventDefault();

  const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
  const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;

  const deltaX = clientX - dragStart.value.x;
  const deltaY = clientY - dragStart.value.y;

  const newCropBox = {
    x: dragStart.value.cropX,
    y: dragStart.value.cropY,
    width: dragStart.value.cropWidth,
    height: dragStart.value.cropHeight,
  };

  if (props.circularOverlay) {
    // Для круглой рамки используем упрощенную логику
    // Определяем изменение размера на основе направления движения
    let sizeChange = 0;

    switch (resizeHandle.value) {
      case "top":
        sizeChange = -deltaY * 2; // Умножаем на 2, так как изменяем размер с обеих сторон
        break;
      case "bottom":
        sizeChange = deltaY * 2;
        break;
      case "left":
        sizeChange = -deltaX * 2;
        break;
      case "right":
        sizeChange = deltaX * 2;
        break;
    }

    // Применяем изменение размера
    const newSize = Math.max(50, dragStart.value.cropWidth + sizeChange);

    // Вычисляем новую позицию для центрирования
    const centerX = dragStart.value.cropX + dragStart.value.cropWidth / 2;
    const centerY = dragStart.value.cropY + dragStart.value.cropHeight / 2;

    newCropBox.width = newSize;
    newCropBox.height = newSize;
    newCropBox.x = centerX - newSize / 2;
    newCropBox.y = centerY - newSize / 2;
  } else {
    // Для прямоугольной рамки применяем изменения в зависимости от ручки
    switch (resizeHandle.value) {
      case "top-left":
        newCropBox.x += deltaX;
        newCropBox.y += deltaY;
        newCropBox.width -= deltaX;
        newCropBox.height -= deltaY;
        break;
      case "top-right":
        newCropBox.y += deltaY;
        newCropBox.width += deltaX;
        newCropBox.height -= deltaY;
        break;
      case "bottom-left":
        newCropBox.x += deltaX;
        newCropBox.width -= deltaX;
        newCropBox.height += deltaY;
        break;
      case "bottom-right":
        newCropBox.width += deltaX;
        newCropBox.height += deltaY;
        break;
      case "top":
        newCropBox.y += deltaY;
        newCropBox.height -= deltaY;
        break;
      case "bottom":
        newCropBox.height += deltaY;
        break;
      case "left":
        newCropBox.x += deltaX;
        newCropBox.width -= deltaX;
        break;
      case "right":
        newCropBox.width += deltaX;
        break;
    }

    // Применяем соотношение сторон для прямоугольной рамки
    const widthChange = Math.abs(newCropBox.width - dragStart.value.cropWidth);
    const heightChange = Math.abs(newCropBox.height - dragStart.value.cropHeight);

    if (widthChange > heightChange) {
      // Подгоняем высоту под ширину
      const newHeight = newCropBox.width / props.aspectRatio;
      if (resizeHandle.value.includes("top")) {
        newCropBox.y = newCropBox.y + newCropBox.height - newHeight;
      }
      newCropBox.height = newHeight;
    } else {
      // Подгоняем ширину под высоту
      const newWidth = newCropBox.height * props.aspectRatio;
      if (resizeHandle.value.includes("left")) {
        newCropBox.x = newCropBox.x + newCropBox.width - newWidth;
      }
      newCropBox.width = newWidth;
    }
  }

  // Проверяем минимальные размеры
  const minSize = 50;
  if (newCropBox.width < minSize || newCropBox.height < minSize) return;

  cropBox.value = newCropBox;
  constrainCropBox();
};

const stopResize = () => {
  isResizing.value = false;

  document.removeEventListener("mousemove", handleResize);
  document.removeEventListener("mouseup", stopResize);
  document.removeEventListener("touchmove", handleResize, { passive: false } as EventListenerOptions);
  document.removeEventListener("touchend", stopResize);
};

// Ограничиваем рамку границами изображения
const constrainCropBox = () => {
  const imageX = imageState.value.x;
  const imageY = imageState.value.y;
  const imageWidth = imageState.value.displayWidth;
  const imageHeight = imageState.value.displayHeight;

  // Ограничиваем позицию
  cropBox.value.x = Math.max(imageX, Math.min(cropBox.value.x, imageX + imageWidth - cropBox.value.width));
  cropBox.value.y = Math.max(imageY, Math.min(cropBox.value.y, imageY + imageHeight - cropBox.value.height));

  // Ограничиваем размер
  const maxWidth = imageX + imageWidth - cropBox.value.x;
  const maxHeight = imageY + imageHeight - cropBox.value.y;

  cropBox.value.width = Math.min(cropBox.value.width, maxWidth);
  cropBox.value.height = Math.min(cropBox.value.height, maxHeight);

  if (props.circularOverlay) {
    // Для круглой рамки поддерживаем квадрат
    const maxSize = Math.min(cropBox.value.width, cropBox.value.height);
    cropBox.value.width = maxSize;
    cropBox.value.height = maxSize;
  } else {
    // Для прямоугольной рамки поддерживаем соотношение сторон после ограничения
    const constrainedHeight = cropBox.value.width / props.aspectRatio;
    if (constrainedHeight <= maxHeight) {
      cropBox.value.height = constrainedHeight;
    } else {
      cropBox.value.height = maxHeight;
      cropBox.value.width = cropBox.value.height * props.aspectRatio;
    }
  }
};

const handleCrop = async () => {
  if (!imageElement.value) return;

  try {
    processing.value = true;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Cannot get canvas context");

    // Размеры выходного изображения
    let outputWidth, outputHeight;

    if (props.circularOverlay) {
      // Для круглой рамки всегда квадратное изображение
      outputWidth = props.outputOptions?.width || 1000;
      outputHeight = outputWidth; // Квадрат
    } else {
      // Для прямоугольной рамки используем заданные размеры или соотношение сторон
      outputWidth = props.outputOptions?.width || 800;
      outputHeight = props.outputOptions?.height || Math.round(outputWidth / props.aspectRatio);
    }

    canvas.width = outputWidth;
    canvas.height = outputHeight;

    // Вычисляем параметры для обрезки
    const image = imageState.value;
    const crop = cropBox.value;

    // Масштаб между натуральным и отображаемым размером
    const scaleX = image.naturalWidth / image.displayWidth;
    const scaleY = image.naturalHeight / image.displayHeight;

    // Позиция рамки обрезки относительно изображения в натуральных пикселях
    const cropX = (crop.x - image.x) * scaleX;
    const cropY = (crop.y - image.y) * scaleY;
    const cropWidth = crop.width * scaleX;
    const cropHeight = crop.height * scaleY;

    // Настраиваем контекст для поворота
    ctx.save();

    if (image.rotation !== 0) {
      ctx.translate(outputWidth / 2, outputHeight / 2);
      ctx.rotate((image.rotation * Math.PI) / 180);
      ctx.translate(-outputWidth / 2, -outputHeight / 2);
    }

    // Рисуем обрезанную часть изображения
    ctx.drawImage(imageElement.value, cropX, cropY, cropWidth, cropHeight, 0, 0, outputWidth, outputHeight);

    ctx.restore();

    // Создаем blob
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error("Failed to create blob"));
        },
        props.outputOptions?.mimeType || "image/jpeg",
        props.outputOptions?.quality || 0.9,
      );
    });

    // Создаем файл
    const fileName = props.file?.name ? `cropped-${props.file.name}` : `cropped-image-${Date.now()}.jpg`;
    const file = new File([blob], fileName, { type: blob.type });
    const dataURL = canvas.toDataURL(
      props.outputOptions?.mimeType || "image/jpeg",
      props.outputOptions?.quality || 0.9,
    );

    const result: CropResult = {
      canvas,
      blob,
      file,
      dataURL,
    };

    emits("crop", result);
    handleClose();
  } catch (err) {
    console.error("Failed to crop image:", err);
    emits("error", "Ошибка обрезки изображения");
  } finally {
    processing.value = false;
  }
};

const handleClose = () => {
  if (imageSrc.value) {
    URL.revokeObjectURL(imageSrc.value);
    imageSrc.value = "";
  }

  loading.value = true;
  error.value = "";

  emits("close");
};

onMounted(() => {
  if (props.file) {
    initializeCropper();
  }
});

onUnmounted(() => {
  if (imageSrc.value) {
    URL.revokeObjectURL(imageSrc.value);
  }
  stopDragCropBox();
  stopResize();
});

watch(
  () => props.file,
  (newFile) => {
    if (newFile) {
      initializeCropper();
    }
  },
);
</script>

<style scoped>
.base-image-cropper {
  display: flex;
  flex-direction: column;
  row-gap: 24px;
  height: 100%;
}

.base-image-cropper__container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.base-image-cropper__image-container {
  position: relative;
  width: 100%;
  height: 60vh;
  max-width: 450px;
  max-height: 300px;
  border-radius: 8px;
  cursor: move;
  background: #f5f5f5;
  overflow: hidden;
}
.base-image-cropper__image {
  position: absolute;
  top: 0;
  left: 0;
  user-select: none;
  pointer-events: none;
  object-fit: contain;
}

.base-image-cropper__controls {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.base-image-cropper__action-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
}
.base-image-cropper__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 24px;
}

.base-image-cropper__error-message {
  color: var(--orange-1);
  text-align: center;
}

.base-image-cropper__loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 10;
}
.crop-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.crop-box {
  position: absolute;
  border: 2px solid #007bff;
  background: transparent;
  cursor: move;
  pointer-events: all;
}
.crop-box::before {
  content: "";
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
}

.resize-handle {
  position: absolute;
  width: 12px;
  height: 12px;
  background: #007bff;
  border: 2px solid white;
  border-radius: 50%;
  z-index: 1;
}

/* Все стили для ручек копируем из BaseImageCropperModal */
.resize-handle.top-left {
  top: -6px;
  left: -6px;
  cursor: nw-resize;
}
.resize-handle.top-right {
  top: -6px;
  right: -6px;
  cursor: ne-resize;
}

.resize-handle.bottom-left {
  bottom: -6px;
  left: -6px;
  cursor: sw-resize;
}

.resize-handle.bottom-right {
  bottom: -6px;
  right: -6px;
  cursor: se-resize;
}

.resize-handle.top-center {
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  cursor: n-resize;
}
.resize-handle.bottom-center {
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  cursor: s-resize;
}

.resize-handle.left-center {
  left: -6px;
  top: 50%;
  transform: translateY(-50%);
  cursor: w-resize;
}

.resize-handle.right-center {
  right: -6px;
  top: 50%;
  transform: translateY(-50%);
  cursor: e-resize;
}

.crop-box--circular .resize-handle.circular-top {
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  cursor: n-resize;
}
.crop-box--circular .resize-handle.circular-right {
  top: 50%;
  right: -6px;
  transform: translateY(-50%);
  cursor: e-resize;
}

.crop-box--circular .resize-handle.circular-bottom {
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  cursor: s-resize;
}

.crop-box--circular .resize-handle.circular-left {
  top: 50%;
  left: -6px;
  transform: translateY(-50%);
  cursor: w-resize;
}

.crop-box--circular {
  border-radius: 50%;
}

.crop-box--circular::before {
  border-radius: 50%;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.6);
}

.icon__button-crop {
  color: var(--grey-3);
  margin-right: 8px;
}

.base-input-file__cropper-actions {
  display: grid;
  grid-auto-flow: column;
  column-gap: 16px;
  margin-top: auto;
}

@media (--desktop) {
  .base-input-file__cropper-actions {
    display: flex;
  }
}
</style>
